import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	ArrowLeft,
	ArrowRight,
	Check,
	User,
	MapPin,
	Briefcase,
	Camera,
	Palette,
	Heart,
	Share,
	Calendar,
} from 'lucide-react';
import { getUser } from '../../store/user';
import useRestorePrivateUserFields from '../../hooks/useRestorePrivateUserFields';
import SublyApi from '../../helpers/Api';
import Button from '../../components/ui/button';
import { useOnboardingImages } from './hooks/useOnboardingImages';
import { 
	validateAllSteps
} from './helpers/OnboardingFieldValidator';
import StepNavigationDropdown from './components/StepNavigationDropdown';

// Import profile card components (shared between onboarding and profile)
import PersonalInfoCard from '../profile/profile-cards/PersonalInfoCard';
import BrokerInfoCard from '../profile/profile-cards/BrokerInfoCard';
import BrandingCard from '../profile/profile-cards/BrandingCard';
import PersonalDetailsCard from '../profile/profile-cards/PersonalDetailsCard';
import SocialMediaCard from '../profile/profile-cards/SocialMediaCard';
import SchedulingCard from '../profile/profile-cards/SchedulingCard';

// Import onboarding-specific components that work conditionally in both contexts
import OnboardingAddressCard from './components/OnboardingAddressCard';
import OnboardingPhotosCard from './components/OnboardingPhotosCard';

const OnboardingWizard = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const currentUser = useSelector((st) => st.user.currentUser);
	const token = useSelector((st) => st.user.token);

	// Wizard state
	const [currentStep, setCurrentStep] = useState(0);
	const [loading, setLoading] = useState(false);
	const [notification, setNotification] = useState({ type: '', message: '' });
	
	// Image management hook
	const clientImages = useOnboardingImages(currentUser);

	// CRM login isn't kept in browser storage; reload it after a refresh (#152)
	useRestorePrivateUserFields();
	const { imagesCounts } = clientImages;
	

	// Check if user has already completed onboarding
	useEffect(() => {
		if (currentUser?.onboardingCompleted === true) {
			history.push('/profile');
		}
	}, [currentUser, history]);

	// Define wizard steps
	const steps = useMemo(() => [
		{
			id: 'personal',
			title: 'Personal Information',
			description: 'Tell us about yourself',
			icon: User,
			component: PersonalInfoCard,
			required: true,
		},
		{
			id: 'address',
			title: 'Contact & Address',
			description: 'Where can we reach you?',
			icon: MapPin,
			component: OnboardingAddressCard,
			required: true,
		},
		{
			id: 'broker',
			title: 'Broker Information',
			description: 'Your professional details',
			icon: Briefcase,
			component: BrokerInfoCard,
			required: true,
		},
		{
			id: 'photos',
			title: 'Photos',
			description: 'Your professional headshot and logo',
			icon: Camera,
			component: OnboardingPhotosCard,
			required: true,
		},
		{
			id: 'branding',
			title: 'Your Branding',
			description: 'Customize your professional look',
			icon: Palette,
			component: BrandingCard,
			required: true,
		},
		{
			id: 'personal-details',
			title: 'Personal Details',
			description: 'Tell us about your interests and values',
			icon: Heart,
			component: PersonalDetailsCard,
			required: true,
		},
		{
			id: 'social',
			title: 'Social Media & Software',
			description: 'Connect your online presence and tools',
			icon: Share,
			component: SocialMediaCard,
			required: true,
		},
		{
			id: 'scheduling',
			title: 'Scheduling & Notes',
			description: 'Set your availability and preferences',
			icon: Calendar,
			component: SchedulingCard,
			required: true,
		},
	], []);

	// Profile data hook for wizard components
	const profileDataHook = {
		// Card-based update method
		updateCard: async (cardData) => {
			setLoading(true);
			setNotification({ type: '', message: '' });

			try {
				// Update user profile via API
				await SublyApi.request(
					`users/${currentUser.username}`,
					cardData,
					'patch',
					{
						Authorization: `Bearer ${token}`,
					}
				);

				// Refresh user data and wait for Redux state to update
				await dispatch(getUser({ username: currentUser.username, token }));

				// Add small delay to ensure Redux state has propagated
				await new Promise(resolve => setTimeout(resolve, 100));

				// Step completion is tracked automatically by validation

				setNotification({
					type: 'success',
					message: 'Information saved successfully!',
				});

				// Clear notification and move to next step after a short delay
				setTimeout(() => {
					setNotification({ type: '', message: '' });
					goNext();
				}, 1500);

				return true;
			} catch (err) {
				console.error('Error updating profile:', err);
				setNotification({
					type: 'error',
					message: err.message || 'Failed to save information',
				});
				return false;
			} finally {
				setLoading(false);
			}
		},

		// Refresh user data from API
		refreshData: async () => {
			try {
				await dispatch(getUser({ username: currentUser.username, token }));
				// Small delay to ensure Redux state propagates
				await new Promise(resolve => setTimeout(resolve, 100));
				return true;
			} catch (err) {
				console.error('Error refreshing user data:', err);
				return false;
			}
		},

		// For onboarding: always show all fields as editable (no individual edit buttons)
		isEditMode: () => true, // Always in edit mode for onboarding
		toggleEditMode: () => {}, // No-op - always editing
		cancelEdit: () => {}, // No-op - always editing

		// Card-based edit mode tracking - always in edit mode for onboarding
		isCardEditMode: () => true,
		toggleCardEditMode: () => {},
		cancelCardEdit: () => {},

		// Special flag to indicate this is onboarding context
		isOnboarding: true,
		// Flag to indicate if wizard is currently loading
		isLoading: loading,
		// Shared clientImages hook to prevent multiple instances
		clientImages,
		// Navigation function for cards that don't need API updates (like photos)
		goToNextStep: () => {
			// Step completion is tracked automatically by validation
			
			setNotification({
				type: 'success',
				message: 'Photos saved successfully!',
			});

			// Clear notification and move to next step after a short delay
			setTimeout(() => {
				setNotification({ type: '', message: '' });
				goNext();
			}, 1500);
		},
	};

	// Handle step navigation
	const goNext = () => {
		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const goPrevious = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};


	// Handle completing onboarding
	const completeOnboarding = async () => {
		setLoading(true);
		try {
			// Mark onboarding as completed
			await SublyApi.request(
				`users/${currentUser.username}`,
				{
					onboarding_completed: true,
					profile_completion_date: new Date().toISOString(),
				},
				'patch',
				{
					Authorization: `Bearer ${token}`,
				}
			);

			// Refresh user data
			await dispatch(getUser({ username: currentUser.username, token }));

			// Add small delay to ensure Redux state updates
			await new Promise(resolve => setTimeout(resolve, 500));

			// Redirect to profile page to show the user their completed profile
			history.push('/profile');
		} catch (err) {
			console.error('Error completing onboarding:', err);
			setNotification({
				type: 'error',
				message: 'Failed to complete onboarding. Please try again.',
			});
		} finally {
			setLoading(false);
		}
	};


	// Calculate field-level validation for all steps
	const validationResults = useMemo(() => {
		if (!currentUser) return null;
		return validateAllSteps(steps, currentUser, imagesCounts);
	}, [currentUser, imagesCounts, steps]);

	// Get current step data
	const currentStepData = steps[currentStep];
	const CurrentStepComponent = currentStepData?.component;

	// Derive all validation and progress from centralized validator
	const progress = validationResults?.overallPercentage || 0;
	const isProfileComplete = validationResults?.isComplete || false;

	// Handle step navigation
	const handleStepChange = useCallback((stepIndex) => {
		if (stepIndex >= 0 && stepIndex < steps.length) {
			setCurrentStep(stepIndex);
		}
	}, [steps.length]);

	// Check if onboarding can be completed
	const canFinishOnboarding = isProfileComplete;

	return (
		<div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4'>
			<div className='w-full max-w-2xl'>
				{/* Header */}
				<div className='text-center mb-8'>
					<h1 className='text-3xl font-bold text-gray-800 font-playfair-display'>
						Welcome to Jane Rothe
					</h1>
					<p className='text-gray-600 mt-2'>
						Let's get your profile set up so you can get started.
					</p>
				</div>

				{/* Progress Bar and Navigation */}
				<div className='mb-4'>
					<div className='flex justify-between items-center mb-2'>
						{/* Step Navigation Dropdown */}
						<StepNavigationDropdown
							steps={steps}
							currentStep={currentStep}
							onStepChange={handleStepChange}
							stepValidations={validationResults?.stepValidations || {}}
							disabled={loading}
						/>
						<span className='text-sm font-medium text-gray-600'>
							Overall Progress: {progress}%
						</span>
					</div>
					<div className='w-full bg-gray-200 rounded-full h-2'>
						<div
							className='bg-gray-800 h-2 rounded-full transition-all duration-300'
							style={{ width: `${progress}%` }}></div>
					</div>
				</div>


				{/* Step Content */}
				<div className='bg-white p-6 md:p-8 rounded-lg shadow-sm w-full'>
					{/* Step Header */}
					<div className='mb-6'>
						<div className='flex items-center gap-3 mb-2'>
							{currentStepData.icon && (
								<currentStepData.icon className='h-6 w-6 text-gray-600' />
							)}
							<h2 className='text-xl font-semibold text-gray-800'>
								{currentStepData.title}
							</h2>
						</div>
						<p className='text-gray-600 text-sm'>
							{currentStepData.description}
						</p>
						
					</div>

					{CurrentStepComponent && (
						<CurrentStepComponent
							key={`step-${currentStep}`} // Force re-mount when step changes
							client={currentUser}
							clientDataHook={profileDataHook}
							currentStep={currentStep} // Pass current step as prop
						/>
					)}
				</div>

				{/* Notification */}
				{notification.message && (
					<div
						className={`mt-4 p-3 rounded-md text-sm ${
							notification.type === 'success'
								? 'bg-green-100 text-green-800'
								: 'bg-red-100 text-red-800'
						}`}>
						{notification.message}
					</div>
				)}

				{/* Navigation */}
				{currentUser && (
					<>
						{/* Finish Onboarding button - shows when profile is 100% complete */}
						{canFinishOnboarding && (
							<div className='mb-4'>
								<Button
									onClick={completeOnboarding}
									disabled={loading}
									className='w-full flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-lg py-3'>
									<Check className='h-5 w-5' />
									🎉 Finish Onboarding - Profile Complete!
								</Button>
								<p className='text-center text-sm text-gray-600 mt-2'>
									Congratulations! You've filled out all required information.
								</p>
							</div>
						)}

						<div className='flex items-center justify-between w-full mt-8'>
							<Button
								onClick={goPrevious}
								disabled={currentStep === 0 || loading}
								variant='outline'
								className='flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 bg-white disabled:opacity-50'>
								<ArrowLeft className='h-4 w-4' />
								Previous
							</Button>

							{currentStep < steps.length - 1 ? (
								<Button
									onClick={goNext}
									disabled={loading}
									variant='outline'
									className='flex items-center gap-2 border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50'>
									Skip to Next
									<ArrowRight className='h-4 w-4' />
								</Button>
							) : (
								<Button
									onClick={completeOnboarding}
									disabled={loading || !canFinishOnboarding}
									className={`flex items-center gap-2 text-white disabled:opacity-50 ${
										canFinishOnboarding 
											? 'bg-green-600 hover:bg-green-700' 
											: 'bg-gray-400 cursor-not-allowed'
									}`}>
									<Check className='h-4 w-4' />
									{canFinishOnboarding ? 'Finish Onboarding' : 'Complete All Fields First'}
								</Button>
							)}
						</div>
						

						{/* Progress indicator */}
						{!canFinishOnboarding && (
							<div className='mt-4 text-center'>
								<p className='text-sm text-gray-600'>
									Fill out all profile information to finish onboarding
								</p>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default OnboardingWizard;
